import { PerformanceData } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import {
  CURRENCY_FORMATTER_MAP,
  formatCAD,
  formatGBP,
  formatMixedCurrency,
  formatMoney,
  formatNumber,
  formatNumberAsInt,
} from "./format-utils";
import * as R from "ramda";
import { CURRENCY_SYMBOL_MAP } from "@blisspointmedia/bpm-types/dist/CurrencySymbols";

interface FormatterOptions {
  decimals?: number;
  zeroLabel?: string;
  min?: number;
  row?: PerformanceData;
  currCode?: string;
}

export interface FormatterNew {
  (value: number, options?: FormatterOptions): string;
}
export type MetricFormatter = (metric: string) => FormatterNew;

/*
 * Standard USD based formatters
 */
export const SPEND_OR_REVENUE_FORMATTER: FormatterNew = (
  spend,
  { decimals = 0, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  spend && !isNaN(spend) && spend >= min
    ? spend > 0 && spend < 1 && min > 0
      ? "<$1"
      : formatMoney(spend, resolveDecimals(decimals))
    : zeroLabel;

export const CPX_FORMATTER: FormatterNew = (
  cpx,
  { decimals, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  cpx && !isNaN(cpx) && cpx >= min
    ? cpx > 0 && cpx < 0.01 && min > 0
      ? "<$0.01"
      : formatMoney(cpx, !R.isNil(decimals) ? resolveDecimals(decimals) : cpx >= 15 ? 0 : 2)
    : zeroLabel;

export const CPM_FORMATTER: FormatterNew = (
  value,
  { decimals = metricDecimalMap.cpm, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  value && !isNaN(value) && value >= min
    ? formatMoney(value, resolveDecimals(decimals))
    : zeroLabel;

/*
 * Metric formatter that don't include a money label
 */
export const toPrettyNumber: FormatterNew = (
  num,
  { decimals = 0, zeroLabel = ZERO_LABEL, min = 0 } = {}
) => (num && !isNaN(num) && num >= min ? formatNumber(num, resolveDecimals(decimals)) : zeroLabel);

export const toPrettyPercent: FormatterNew = (
  num,
  { decimals = 0, zeroLabel = ZERO_LABEL, min = 0 } = {}
) =>
  num && !isNaN(num) && Math.abs(num) >= min
    ? `${formatNumber(num * 100.0, resolveDecimals(decimals))}%`
    : zeroLabel;

export const toPrettyPercent2Decimals: FormatterNew = (
  num: number,
  { decimals = 2, zeroLabel = ZERO_LABEL, min = 0 } = {}
) => toPrettyPercent(num, { decimals, min, zeroLabel });

export const SPAM_REPORT_RATE_FORMATTER: FormatterNew = (
  num: number,
  { decimals = 2, zeroLabel = "<0.01%", min = 0.01 } = {}
) => toPrettyPercent(num, { decimals, min, zeroLabel });

// Shows "+" for positive numbers
export const toPrettyDeltaPercent: FormatterNew = (
  num,
  { decimals = 0, zeroLabel = ZERO_LABEL, min = 0 } = {}
) =>
  num && !isNaN(num) && Math.abs(num) >= min
    ? `${formatNumber(num * 100.0, resolveDecimals(decimals), "exceptZero")}%`
    : zeroLabel;

/*
 * Includes an "override" decimal value to format as int
 */
export const IMP_IN_THOUSANDS_FORMATTER: FormatterNew = (
  num,
  { zeroLabel = ZERO_LABEL, decimals, min = 0 } = {}
) => {
  if (decimals === -1) {
    return IMP_FORMATTER(num, { zeroLabel, min });
  }
  return num && !isNaN(num) && num / 1000.0 > 0 && num >= min
    ? formatNumberAsInt((num || 0) / 1000)
    : zeroLabel;
};

export const IMP_FORMATTER: FormatterNew = (
  value,
  { zeroLabel = ZERO_LABEL, min = 0 } = {}
): string => (value && !isNaN(value) && value >= min ? formatNumberAsInt(value) : zeroLabel);

export const ROAS_FORMATTER: FormatterNew = (
  num,
  { decimals = metricDecimalMap.roas, zeroLabel = ZERO_LABEL, min = 0 } = {}
) =>
  num && !isNaN(num) && num >= min
    ? num > 0 && num < 0.01
      ? "<0.01"
      : formatNumber(num, resolveDecimals(decimals))
    : zeroLabel;

export const CLICKS_FORMATTER: FormatterNew = (value, { row } = {}) => {
  if (
    ["Linear", "Streaming Video", "Video"].includes(`${R.path(["dimensions", "Channel"], row)}`) &&
    value < 1
  ) {
    return "--";
  }
  return value > 0 && value < 1 ? "<1" : formatNumberAsInt(value);
};

export const RELATIVE_FORMATTER: FormatterNew = (value, { zeroLabel = ZERO_LABEL, min = 0 } = {}) =>
  value && !isNaN(value) && value >= min ? `${Math.round(value).toLocaleString()}%` : zeroLabel;

export const VOLUME_FORMATTER: FormatterNew = (value, { zeroLabel = ZERO_LABEL, min = 0 } = {}) =>
  value && !isNaN(value) && value >= min
    ? value > 0 && value < 1
      ? "<1"
      : formatNumberAsInt(value)
    : zeroLabel;

const ZERO_LABEL = "--";
const MAX_DECIMALS = 4;
export const resolveDecimals = (decimals: number): number => Math.min(MAX_DECIMALS, decimals);

/*
 * Native money metric formatters
 */
export const FOREIGN_SPEND_OR_REVENUE_FORMATTER = (
  spend: number,
  { currCode = "", decimals = 0, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
): string => {
  const symbol = CURRENCY_SYMBOL_MAP[currCode] || "mixed ";
  const formatter = CURRENCY_FORMATTER_MAP[currCode] || formatMixedCurrency;

  return spend && !isNaN(spend) && spend >= min
    ? spend > 0 && spend < 1
      ? `<${symbol}1`
      : formatter(spend, resolveDecimals(decimals))
    : zeroLabel;
};

export const NATIVE_SPEND_OR_REVENUE_FORMATTER: FormatterNew = (
  value: number,
  options = {}
): string => {
  const { currCode, ...args } = options;
  if (!currCode || currCode === "" || currCode === "USD") {
    return SPEND_OR_REVENUE_FORMATTER(value, { ...args });
  } else {
    return FOREIGN_SPEND_OR_REVENUE_FORMATTER(value, { currCode, ...args });
  }
};

export const FOREIGN_CPX_FORMATTER: FormatterNew = (
  value: number,
  { decimals, currCode = "", zeroLabel = ZERO_LABEL, min = 0.01 } = {}
): string => {
  const symbol = CURRENCY_SYMBOL_MAP[currCode] || "mixed ";
  const formatter = currCode
    ? CURRENCY_FORMATTER_MAP[currCode] || formatMixedCurrency
    : formatMixedCurrency;
  return value && !isNaN(value) && value >= min
    ? value > 0 && value < 0.01
      ? `<${symbol}0.01`
      : formatter(value, !R.isNil(decimals) ? resolveDecimals(decimals) : value >= 15 ? 0 : 2)
    : zeroLabel;
};

export const NATIVE_CPX_FORMATTER: FormatterNew = (value: number, options = {}): string => {
  const { currCode, ...args } = options;
  if (!currCode || currCode === "" || currCode === "USD") {
    return CPX_FORMATTER(value, { ...args });
  } else {
    return FOREIGN_CPX_FORMATTER(value, { currCode, ...args });
  }
};

export const FOREIGN_CPM_FORMATTER: FormatterNew = (
  value: number,
  { decimals = metricDecimalMap.cpm, currCode, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
): string => {
  const formatter = currCode
    ? CURRENCY_FORMATTER_MAP[currCode] || formatMixedCurrency
    : formatMixedCurrency;
  return value && !isNaN(value) && value >= min
    ? formatter(value, resolveDecimals(decimals))
    : zeroLabel;
};

export const NATIVE_CPM_FORMATTER: FormatterNew = (value: number, options = {}): string => {
  const { currCode, ...args } = options;
  if (!currCode || currCode === "" || currCode === "USD") {
    return CPM_FORMATTER(value, { ...args });
  } else {
    return FOREIGN_CPM_FORMATTER(value, { currCode, ...args });
  }
};

/*
 * Standard CAD based formatters -- specifically for saxx
 */
export const SPEND_OR_REVENUE_FORMATTER_CAD: FormatterNew = (
  spend: number,
  { decimals = 0, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  spend && !isNaN(spend) && spend >= min
    ? spend > 0 && spend < 1
      ? "<CA$1"
      : formatCAD(spend, resolveDecimals(decimals))
    : zeroLabel;

export const CPM_FORMATTER_CAD: FormatterNew = (
  value: number,
  { decimals = metricDecimalMap.cpm, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
): string =>
  value && !isNaN(value) && value >= min ? formatCAD(value, resolveDecimals(decimals)) : zeroLabel;

export const CPX_FORMATTER_CAD: FormatterNew = (
  cpx: number,
  { decimals, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  cpx && !isNaN(cpx) && cpx >= min
    ? cpx > 0 && cpx < 0.01
      ? "<CA$0.01"
      : formatCAD(cpx, !R.isNil(decimals) ? resolveDecimals(decimals) : cpx >= 15 ? 0 : 2)
    : zeroLabel;

/*
 * Standard GBP based formatters -- specifically for muraduk
 */
export const SPEND_OR_REVENUE_FORMATTER_GBP: FormatterNew = (
  spend: number,
  { decimals = 0, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  spend && !isNaN(spend) && spend >= min
    ? spend > 0 && spend < 1
      ? "<£1"
      : formatGBP(spend, resolveDecimals(decimals))
    : zeroLabel;

export const CPM_FORMATTER_GBP: FormatterNew = (
  value: number,
  { decimals = metricDecimalMap.cpm, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
): string =>
  value && !isNaN(value) && value >= min ? formatGBP(value, resolveDecimals(decimals)) : zeroLabel;

export const CPX_FORMATTER_GBP: FormatterNew = (
  cpx: number,
  { decimals, zeroLabel = ZERO_LABEL, min = 0.01 } = {}
) =>
  cpx && !isNaN(cpx) && cpx >= min
    ? cpx > 0 && cpx < 0.01
      ? "<£0.01"
      : formatGBP(cpx, !R.isNil(decimals) ? resolveDecimals(decimals) : cpx >= 15 ? 0 : 2)
    : zeroLabel;

export const METRIC_TO_FORMATTER: { [key: string]: FormatterNew } = {
  acos: ROAS_FORMATTER,
  aov: toPrettyNumber,
  clicks: CLICKS_FORMATTER,
  clicksConversionRate: toPrettyPercent2Decimals,
  costPerConversion: CPX_FORMATTER,
  cpc: CPX_FORMATTER,
  eCpm: CPM_FORMATTER,
  youtubeCpm: CPM_FORMATTER,
  cpm: CPM_FORMATTER,
  cpx: CPX_FORMATTER,
  ctr: toPrettyPercent2Decimals,
  imps: IMP_FORMATTER,
  impressions: IMP_FORMATTER,
  impressionShare: toPrettyPercent2Decimals,
  impsConversionRate: toPrettyPercent2Decimals,
  number: toPrettyNumber,
  percentage: toPrettyPercent,
  relative: RELATIVE_FORMATTER,
  revenue: SPEND_OR_REVENUE_FORMATTER,
  roas: ROAS_FORMATTER,
  spend: SPEND_OR_REVENUE_FORMATTER,
  topImpressionShare: toPrettyPercent2Decimals,
  views: IMP_IN_THOUSANDS_FORMATTER,
  volume: VOLUME_FORMATTER,
  ctrEmail: toPrettyPercent2Decimals,
  deliveries: CLICKS_FORMATTER,
  sends: CLICKS_FORMATTER,
  opens: CLICKS_FORMATTER,
  ctor: toPrettyPercent2Decimals,
  revenuePerDelivered: SPEND_OR_REVENUE_FORMATTER,
  bounceRate: toPrettyPercent2Decimals,
  openRate: toPrettyPercent2Decimals,
  unsubscribeRate: toPrettyPercent2Decimals,
  clickToOpenRate: toPrettyPercent2Decimals,
  spamReportRate: SPAM_REPORT_RATE_FORMATTER,
  // Native formatters
  cpcNative: NATIVE_CPX_FORMATTER,
  cpmNative: NATIVE_CPM_FORMATTER,
  cpxNative: NATIVE_CPX_FORMATTER,
  revenue_native: NATIVE_SPEND_OR_REVENUE_FORMATTER,
  spend_native: NATIVE_SPEND_OR_REVENUE_FORMATTER,
};

export const METRIC_TO_FORMATTER_CAD = {
  cpc: CPX_FORMATTER_CAD,
  cpm: CPM_FORMATTER_CAD,
  cpx: CPX_FORMATTER_CAD,
  revenue: SPEND_OR_REVENUE_FORMATTER_CAD,
  spend: SPEND_OR_REVENUE_FORMATTER_CAD,
  costPerConversion: CPX_FORMATTER_CAD,
};

export const METRIC_TO_FORMATTER_GBP = {
  cpc: CPX_FORMATTER_GBP,
  cpm: CPM_FORMATTER_GBP,
  cpx: CPX_FORMATTER_GBP,
  revenue: SPEND_OR_REVENUE_FORMATTER_GBP,
  spend: SPEND_OR_REVENUE_FORMATTER_GBP,
  costPerConversion: CPX_FORMATTER_GBP,
};

const STANDARD_MONEY_METRICS = new Set([
  "cpc",
  "cpm",
  "cpx",
  "revenue",
  "spend",
  "costPerConversion",
]);

/*
 * Default decimals for metrics, if not listed 0 is default
 */
export const metricDecimalMap = {
  acos: 2,
  clicksConversionRate: 2,
  cpm: 2,
  ctr: 2,
  impressionShare: 2,
  impsConversionRate: 2,
  roas: 2,
  topImpressionShare: 2,
  cpmNative: 2,
};

/**
 * Returns the formatter function for the provided metric with option to use CAD.
 */
export const metricFormatter = (metric: string, showInCAD?: boolean): FormatterNew => {
  if (showInCAD && STANDARD_MONEY_METRICS.has(metric)) {
    return METRIC_TO_FORMATTER_CAD[metric];
  } else if (METRIC_TO_FORMATTER[metric]) {
    return METRIC_TO_FORMATTER[metric];
  } else {
    return METRIC_TO_FORMATTER.volume;
  }
};

/**
 * Returns the formatter function for the provided metric always in USD
 */
export const metricFormatterStandard: MetricFormatter = metric => {
  if (METRIC_TO_FORMATTER[metric]) {
    return METRIC_TO_FORMATTER[metric];
  } else {
    return METRIC_TO_FORMATTER.volume;
  }
};

/**
 * Returns the formatter function for the provided metric always in CAD, specially for saxx
 */
export const metricFormatterCAD: MetricFormatter = metric => {
  if (STANDARD_MONEY_METRICS.has(metric)) {
    return METRIC_TO_FORMATTER_CAD[metric];
  } else if (METRIC_TO_FORMATTER[metric]) {
    return METRIC_TO_FORMATTER[metric];
  } else {
    return METRIC_TO_FORMATTER.volume;
  }
};

/**
 * Returns the formatter function for the provided metric always in CAD, specially for saxx
 */
export const metricFormatterGBP: MetricFormatter = metric => {
  if (STANDARD_MONEY_METRICS.has(metric)) {
    return METRIC_TO_FORMATTER_GBP[metric];
  } else if (METRIC_TO_FORMATTER[metric]) {
    return METRIC_TO_FORMATTER[metric];
  } else {
    return METRIC_TO_FORMATTER.volume;
  }
};

/**
 * Returns the metricFormatter adjusted to currency code
 */
export const getMetricFormatter = (company: string): MetricFormatter =>
  showCAD(company)
    ? metricFormatterCAD
    : showGBP(company)
    ? metricFormatterGBP
    : metricFormatterStandard;

export const showCAD = (company: string): boolean => ["saxx"].includes(company);

export const showGBP = (company: string): boolean =>
  ["muraduk", "livingproofuk", "tatchauk"].includes(company);

export const companyCurrencySymbol = (company: string): string =>
  showCAD(company) ? "CA$" : showGBP(company) ? "£" : "$";
